// StudentLeavePanel — school-wide student leave: who is away on a date,
// pending requests to approve, and recent leave. Leave is recorded on each
// student's profile (Leave tab).
import { useEffect, useState } from 'react';
import { getAllStudentLeaves, decideStudentLeave } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';
import { Msg, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import { fmtDate, todayISO } from '../ui/format';

const TONE = { approved: 'success', pending: 'caution', rejected: 'critical' };

export default function StudentLeavePanel({ onOpenStudent }) {
  const [view, setView] = useState('today');
  const [date, setDate] = useState(todayISO);
  const [rows, setRows] = useState([]);
  const [msg,  setMsg]  = useState('');

  useEffect(() => { load(); }, [view, date]);
  async function load() {
    const params = view === 'today' ? { on: date, status: 'approved' } : view === 'pending' ? { status: 'pending' } : {};
    try { setRows(await getAllStudentLeaves(params)); } catch (err) { setMsg(errText(err)); }
  }
  async function decide(l, status) {
    try { await decideStudentLeave(l.id, { status, notify: true }); setMsg(`Leave ${status} — parent notified ✅`); load(); }
    catch (err) { setMsg(errText(err)); }
  }

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Student Leave</h2>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1.5 bg-transit-tint p-1 rounded-m">
          {[['today', 'On leave'], ['pending', 'Pending requests'], ['all', 'All recent']].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} className={`px-4 py-2 rounded-s text-[13px] font-bold ${view === k ? 'bg-transit text-white shadow-1' : 'text-transit-deep'}`}>{label}</button>
          ))}
        </div>
        {view === 'today' && <input type="date" className={inputCls} value={date} onChange={e => setDate(e.target.value || todayISO())} aria-label="Date" />}
      </div>
      <Msg msg={msg} className="mb-3" />
      <Card>
        <CardHeader title={view === 'today' ? `${rows.length} student${rows.length === 1 ? '' : 's'} on leave ${date === todayISO() ? 'today' : `on ${fmtDate(date)}`}` : `${rows.length} leave record${rows.length === 1 ? '' : 's'}`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Student</th><th className="px-4 py-2.5">Dates</th><th className="px-4 py-2.5">Reason</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5"></th></tr></thead>
            <tbody>
              {rows.map(l => (
                <tr key={l.id} className="border-t border-line-soft">
                  <td className="px-4 py-3"><button onClick={() => onOpenStudent(l.student_id, 'leave')} className="font-semibold text-transit-deep hover:underline">{l.student_name}</button>
                    <span className="block text-xs text-ink-soft">Class {l.class_name || '—'}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(l.from_date)}{l.to_date !== l.from_date ? ` – ${fmtDate(l.to_date)}` : ''}<span className="block text-xs text-ink-soft">{l.days} day{l.days === 1 ? '' : 's'}</span></td>
                  <td className="px-4 py-3">{l.reason}</td>
                  <td className="px-4 py-3"><Badge tone={TONE[l.status]}>{l.status}</Badge></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {l.status === 'pending' && <div className="flex gap-1.5">
                      <Button className="!px-2.5 !py-1 !text-xs" onClick={() => decide(l, 'approved')}>Approve</Button>
                      <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => decide(l, 'rejected')}>Reject</Button>
                    </div>}
                  </td>
                </tr>
              ))}
              {!rows.length && <EmptyRow cols={5}>Nothing to show. Record a student's leave from their profile (Leave tab).</EmptyRow>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
